import { Box, Button, Checkbox, Flex } from "@eworkit/iris";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useDialogState } from "reakit/Dialog";

import { ConfirmationDialogModal } from "@/components/ConfirmModal";
import { Header } from "@/components/Header";
import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { TextListWithDot } from "@/components/TextListWithDot";
import { UserContext } from "@/components/User";
import { fetchJobApplicationsCompact, IJobApplication } from "@/data/job-applications";
import { joinCompany } from "@/data/users";
import { ClickableSection } from "@/infrastructure/Route";
import { useToasts } from "@/views/components/Toast/useToasts";

interface IApplicationsSectionProps {
  onClose: () => void;
  companyId: number;
}

const ApplicationsSection = ({ onClose, companyId }: IApplicationsSectionProps) => {
  const { addToast } = useToasts();
  const { t } = useTranslation("session");
  const confirmModalDialogState = useDialogState({ animated: true });

  const [submittingApplications, setSubmittingApplications] = useState(false);

  const user = useContext(UserContext);
  const { data, status } = useQuery(String(user.sessionUser.id), () => fetchJobApplicationsCompact({}));

  const [applications, setApplications] = useState<IJobApplication[]>();
  const [selectedApplications, setSelectedApplications] = useState<number[]>([]);

  const loadingApplications = status === "loading";

  const handleClick = (appId: number) => {
    selectedApplications.includes(appId)
      ? setSelectedApplications(prev => prev.filter(id => id !== appId))
      : setSelectedApplications(prev => [...prev, appId]);
  };

  const handleCancel = () => {
    addToast({
      text: t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.TOAST.CANCEL"),
      type: "information",
    });

    onClose();
  };

  const handleSubmit = async () => {
    setSubmittingApplications(true);

    try {
      await joinCompany({ applicationIds: selectedApplications, companyId });

      addToast({
        text: t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.TOAST.CONFIRM"),
        type: "success",
      });

      onClose();
    } catch (e) {
      addToast({
        text: t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.TOAST.FAIL"),
        type: "danger",
      });
    } finally {
      setSubmittingApplications(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedApplications.length !== applications?.length) {
      const allApplicationIds = applications?.map(app => app.id) || [];

      setSelectedApplications(allApplicationIds);
    }
  };

  const handleClear = () => {
    setSelectedApplications([]);
  };

  useEffect(() => {
    if (status === "success") {
      setApplications(data?.content);
    } else if (status === "error") {
      addToast({
        text: t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.TOAST.FETCH_FAIL"),
        type: "danger",
      });
    }
  }, [addToast, data?.content, status, t]);

  return (
    <>
      <Box p="xs" mt="xs">
        <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.DESCRIPTION")}</Text>
      </Box>

      <Flex justifyContent="flex-end" gap="sm">
        <Button
          variant="ghost"
          onClick={() => handleSelectAll()}
          size="sm"
          disabled={loadingApplications || submittingApplications}>
          <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.BUTTONS.SELECT_ALL")}</Text>
        </Button>

        <Button
          variant="ghost"
          onClick={() => handleClear()}
          size="sm"
          disabled={loadingApplications || submittingApplications}>
          <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.BUTTONS.CLEAR")}</Text>
        </Button>
      </Flex>

      {loadingApplications ? (
        <Flex mt="sm" mb="sm" justifyContent="center">
          <LoadingSpinner size="md" variant="primary" />
        </Flex>
      ) : (
        <Box
          style={{
            maxHeight: "245px",
            overflowY: "auto",
            overflowX: "hidden",
          }}>
          {applications && applications?.length > 0 ? (
            applications.map(app => (
              <ClickableSection
                key={app.jobRequest.id}
                borderRadius
                margin={["xs"]}
                onClick={() => handleClick(app.id)}>
                <Flex
                  p="sm"
                  borderRadius="md"
                  flexDirection="row"
                  alignItems="center"
                  borderLeft="primary"
                  borderRight="primary"
                  justifyContent="space-between">
                  <Box>
                    <Header size="sm" text={app.jobRequest.title} weight="bold" />
                    <TextListWithDot>
                      <Text>{app.jobRequest.systemId}</Text>
                      <Text>{app.jobRequest.client?.name}</Text>
                    </TextListWithDot>
                  </Box>

                  <Checkbox checked={selectedApplications.includes(app.id)} />
                </Flex>
              </ClickableSection>
            ))
          ) : (
            <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.NO_APPLICATIONS")}</Text>
          )}
        </Box>
      )}

      <Flex justifyContent="space-between" mt="sm" alignItems="center">
        <Text color="grey2">{`${selectedApplications.length} selected`}</Text>

        <Flex gap="sm">
          <Button
            variant="secondary"
            onClick={confirmModalDialogState.show}
            size="lg"
            disabled={submittingApplications}>
            <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.BUTTONS.CANCEL")}</Text>
          </Button>

          <Button
            loading={submittingApplications}
            variant="primary"
            disabled={submittingApplications || !selectedApplications.length}
            onClick={async () => {
              await handleSubmit();
            }}
            size="lg">
            <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.APPLICATIONS_SECTION.BUTTONS.CONFIRM")}</Text>
          </Button>
        </Flex>
      </Flex>

      <ConfirmationDialogModal
        onAccept={handleCancel}
        uppercaseButtonLabel={false}
        onClose={confirmModalDialogState.hide}
        open={confirmModalDialogState.visible}
        header={t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.CONFIRMATION_DIALOG.HEADER")}
        cancelButtonLabel={t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.CONFIRMATION_DIALOG.CANCEL")}
        acceptButtonLabel={t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.CONFIRMATION_DIALOG.CONFIRM")}
        confirmationText={t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.CONFIRMATION_DIALOG.DESCRIPTION")}
      />
    </>
  );
};

export default ApplicationsSection;
